"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { handleSilentError } from "@/shared/api/error-handler";
import { invokeTauri, checkIsTauri, listenTauri } from "@/shared/lib/tauri";
import type {
  GameflowPhase,
  LcuStatus,
  LiveGameEvent,
  MatchResult,
  ResultSubmittedEvent,
  SetActiveMatchRequest,
  SetActive1v1MatchRequest,
  WinCondition1v1Event,
} from "@/shared/types/tauri";

const POLL_INTERVAL = 3000;

interface UseLcuStatusReturn {
  isDesktop: boolean;
  status: LcuStatus | null;
  matchResult: MatchResult | null;
  liveGame: LiveGameEvent | null;
  lastSubmitEvent: ResultSubmittedEvent | null;
  seriesScore: [number, number];
  win1v1: WinCondition1v1Event | null;
  refreshStatus: () => Promise<void>;
  setActiveMatch: (req: SetActiveMatchRequest) => Promise<void>;
  clearActiveMatch: () => Promise<void>;
  setActive1v1Match: (req: SetActive1v1MatchRequest) => Promise<void>;
  clearActive1v1Match: () => Promise<void>;
}

export function useLcuStatus(): UseLcuStatusReturn {
  const [status, setStatus] = useState<LcuStatus | null>(null);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const [liveGame, setLiveGame] = useState<LiveGameEvent | null>(null);
  const [lastSubmitEvent, setLastSubmitEvent] =
    useState<ResultSubmittedEvent | null>(null);
  const [seriesScore, setSeriesScore] = useState<[number, number]>([0, 0]);
  const [win1v1, setWin1v1] = useState<WinCondition1v1Event | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshStatus = useCallback(async () => {
    if (!checkIsTauri()) return;
    try {
      const s = await invokeTauri<LcuStatus>("get_lcu_status");
      setStatus(s);
      if (s.hasMatchData) {
        const match = await invokeTauri<MatchResult | null>("get_match_data");
        if (match) setMatchResult(match);
      }
    } catch {
      // Tauri not available
    }
  }, []);

  const setActiveMatch = useCallback(async (req: SetActiveMatchRequest) => {
    if (!checkIsTauri()) return;
    try {
      await invokeTauri("set_active_match", { req });
      setSeriesScore([0, 0]);
      setLastSubmitEvent(null);
      setMatchResult(null);
    } catch (e) {
      handleSilentError(e, {
        component: "use-lcu-status",
        action: "set_active_match",
      });
    }
  }, []);

  const clearActiveMatch = useCallback(async () => {
    if (!checkIsTauri()) return;
    try {
      await invokeTauri("clear_active_match");
      setSeriesScore([0, 0]);
      setLastSubmitEvent(null);
    } catch (e) {
      handleSilentError(e, {
        component: "use-lcu-status",
        action: "clear_active_match",
      });
    }
  }, []);

  const setActive1v1Match = useCallback(
    async (req: SetActive1v1MatchRequest) => {
      if (!checkIsTauri()) return;
      try {
        await invokeTauri("set_active_1v1_match", { req });
        setSeriesScore([0, 0]);
        setWin1v1(null);
        setLastSubmitEvent(null);
        setMatchResult(null);
      } catch (e) {
        handleSilentError(e, {
        component: "use-lcu-status",
        action: "set_active_1v1_match",
      });
      }
    },
    []
  );

  const clearActive1v1Match = useCallback(async () => {
    if (!checkIsTauri()) return;
    try {
      await invokeTauri("clear_active_1v1_match");
      setWin1v1(null);
      setSeriesScore([0, 0]);
    } catch (e) {
      handleSilentError(e, {
        component: "use-lcu-status",
        action: "clear_active_1v1_match",
      });
    }
  }, []);

  useEffect(() => {
    if (!checkIsTauri()) return;

    refreshStatus();

    intervalRef.current = setInterval(refreshStatus, POLL_INTERVAL);

    const cleanups: (() => void)[] = [];

    listenTauri<LcuStatus>("lcu:status", (payload) => {
      setStatus(payload);
    }).then((unlisten) => {
      if (unlisten) cleanups.push(unlisten);
    });

    listenTauri<GameflowPhase>("lcu:phase-changed", () => {
      refreshStatus();
    }).then((unlisten) => {
      if (unlisten) cleanups.push(unlisten);
    });

    listenTauri<MatchResult>("lcu:match-result", (payload) => {
      setMatchResult(payload);
    }).then((unlisten) => {
      if (unlisten) cleanups.push(unlisten);
    });

    listenTauri<LiveGameEvent>("lcu:live-game", (payload) => {
      setLiveGame(payload);
    }).then((unlisten) => {
      if (unlisten) cleanups.push(unlisten);
    });

    listenTauri<ResultSubmittedEvent>("lcu:result-submitted", (payload) => {
      setLastSubmitEvent(payload);
      if (payload.success && payload.seriesScore) {
        setSeriesScore(payload.seriesScore);
      }
      if (payload.success) {
        setMatchResult(null);
      }
    }).then((unlisten) => {
      if (unlisten) cleanups.push(unlisten);
    });

    listenTauri<WinCondition1v1Event>("lcu:1v1-win-detected", (payload) => {
      setWin1v1(payload);
    }).then((unlisten) => {
      if (unlisten) cleanups.push(unlisten);
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      cleanups.forEach((fn) => fn());
    };
  }, [refreshStatus]);

  return {
    isDesktop: checkIsTauri(),
    status,
    matchResult,
    liveGame,
    lastSubmitEvent,
    seriesScore,
    win1v1,
    refreshStatus,
    setActiveMatch,
    clearActiveMatch,
    setActive1v1Match,
    clearActive1v1Match,
  };
}
